import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import store from 'utils/store';
import { Section, Container, Typography, List } from 'components/base';
import {
  SignInWithAmazon,
  SignInWithEmail,
  SignInWithFacebook,
  SignInWithAncestry,
  SignInWithFacebookToggler,
  SignInWithAmazonToggler,
  SignInWithEmailToggler,
  SignInWithFamilySearch
} from 'ecommerce/components';
import withAmazon from 'ecommerce/hoc/withAmazon';
import withFacebook from 'ecommerce/hoc/withFacebook';
import authApi from 'ecommerce/api/auth';
import { useApiHandler } from 'ecommerce/lib/useApi';
import useModalContext from 'components/context/modal';
import useDataContext from 'data/context/data';
import useUserContext from 'ecommerce/context/user';

import { Head, Layout } from '../components/layout';
import tracker from '../lib/tracker';

const SESSION_REDIRECT_KEY = 'mycanvas.signin.redirect';

const SignInPage = props => {
  const dataContext = useDataContext();
  const userContext = useUserContext();
  const modalContext = useModalContext();
  const router = useRouter();

  let redirectUrl;
  if (router.asPath.indexOf('/signin') === 0) {
    if (store.session.has(SESSION_REDIRECT_KEY)) {
      redirectUrl = store.session.get(SESSION_REDIRECT_KEY, '/');
    } else {
      redirectUrl = '/';
    }
  } else {
    redirectUrl = router.asPath;
  }

  useEffect(() => {
    if (router.asPath.indexOf('/signin') !== 0) {
      store.session.set(SESSION_REDIRECT_KEY, router.asPath);
    }
    return () => {
      store.session.remove(SESSION_REDIRECT_KEY);
    };
  }, [router.asPath]);

  const setData = async (data, type = 'signin') => {
    userContext.setAccessToken(data.access_token);

    modalContext.toast(
      `You have been successfully ${type === 'signup' ? 'signed up' : 'signed in'}!`,
      {
        type: 'success',
      },
    );

    await router.replace(redirectUrl);
    window.scrollTo(0, 0);
  };

  //
  // SIGN IN WITH EMAIL
  //
  const handleOnSignInWithEmailClick = useApiHandler(async (email, password) => {
    const { data, error } = await authApi(dataContext.countryCode).signIn(email, password);

    if (error) {
      let errorMessage;
      if (error.code === 401) {
        errorMessage = error.body;
      }
      tracker.onCustomEvent('signin_email_error');
      return { success: false, error: errorMessage };
    }

    await setData(data);
    tracker.signIn(data);
    return { success: true };
  });

  const handleOnSignUpWithEmailClick = useApiHandler(async (email, password) => {
    const { data, error } = await authApi(dataContext.countryCode).signUp(email, password);

    if (error) {
      tracker.onCustomEvent('signup_email_error');
      return { success: false };
    }

    await setData(data, 'signup');
    tracker.signUp(data);
    return { success: true };
  });

  const handleOnForgotPasswordClick = useApiHandler(async email => {
    const { error } = await authApi(dataContext.countryCode).resetPassword(email);

    if (error) {
      return { success: false };
    }

    return { success: true };
  });

  //
  // SIGN IN WITH AMAZON
  //
  const handleOnAmazonSignInSuccess = async amazonUserData => {
    const { data, error } = await authApi(dataContext.countryCode).signInWithAmazon(
      amazonUserData.id,
      amazonUserData.token,
      amazonUserData.email,
    );

    if (data) {
      setData(data);
      tracker.signInWithAmazon(data);
    } else {
      tracker.onCustomEvent('signin_amazon_error');
      console.error(error);
      modalContext.toast(
        'Problem with sign in with Amazon. Please try again or use different method.',
        {
          type: 'warning',
        },
      );
    }
  };

  //
  // SIGN IN WITH FACEBOOK
  //
  const handleOnFacebookSignInSuccess = async facebookUserData => {
    const { data, error } = await authApi(dataContext.countryCode).signInWithFacebook(
      facebookUserData.id,
      facebookUserData.name,
      facebookUserData.email,
    );

    if (data) {
      setData(data);
      tracker.signInWithFacebook(data);
    } else {
      tracker.onCustomEvent('signin_facebook_error');
      console.error(error);
      modalContext.toast(
        'Problem with sign in with Facebook. Please try again or use different method.',
        {
          type: 'warning',
        },
      );
    }
  };

  const toggler = useState();

  const showFamilySearch = true //router.query.familysearch != null || router.query.source === 'familysearch';

  return (
    <>
      <Head title="Login" />

      <Layout className="d-flex">
        <Section
          className="flex-grow-1"
          style={{ background: 'no-repeat center top url(/static/img/canvas-blue-bg.svg)' }}
        >
          <Container>
            <Typography.H2 className="text-center mb-5">LOGIN</Typography.H2>
            <Typography.P className="text-center mb-3">
              {showFamilySearch ?
                <>Login with Ancestry or FamilySearch to create projects with your family trees.</> :
                <>Login with Ancestry to create projects with your Ancestry trees.</>
              }
            </Typography.P>

            <div className="w-100 mx-auto mw-400">
              <SignInWithAncestry redirectUrl={redirectUrl} className="mt-1 text-center w-100" />

              {showFamilySearch &&
                <SignInWithFamilySearch redirectUrl={redirectUrl} className="mt-1 text-center w-100" />
              }

              <Typography.Span className="small-decorated my-5">
                <Typography.Span>Or</Typography.Span>
              </Typography.Span>
            </div>

            <Typography.P className="text-center mb-3">
              {showFamilySearch
                ? <>Don’t have an Ancestry nor FamilySearch account? Sign in with your E-mail or Amazon.</>
                : <>Don’t have an Ancestry account? Sign in with your E-mail or Amazon.</>
              }

            </Typography.P>

            <div className="w-100 mx-auto mw-400">
              <div className="px-0 px-sm-2">
                <SignInWithEmailToggler toggler={toggler} className="mt-1 mb-1">
                  <SignInWithEmail
                    onSignIn={handleOnSignInWithEmailClick}
                    onSignUp={handleOnSignUpWithEmailClick}
                    onForgetPassword={handleOnForgotPasswordClick}
                  />
                </SignInWithEmailToggler>

                {/* <SignInWithFacebookToggler toggler={toggler} className="mb-1">
                  <List styled>
                    <List.Item className="small">We do not post on Facebook</List.Item>
                  </List>

                  <SignInWithFacebook
                    fb={props.fb}
                    onSuccess={handleOnFacebookSignInSuccess}
                    className="mt-4 text-center"
                  />
                </SignInWithFacebookToggler> */}

                <SignInWithAmazonToggler toggler={toggler}>
                  <List styled>
                    <List.Item className="small">
                      Use your Amazon.com account to sign in when you shop with MyCanvas
                    </List.Item>
                    <List.Item className="small">
                      Pay for your purchases knowing you’ve got the protection of Amazon
                    </List.Item>
                  </List>

                  <SignInWithAmazon
                    onSuccess={handleOnAmazonSignInSuccess}
                    className="mt-4 text-center"
                  />
                </SignInWithAmazonToggler>
              </div>
            </div>
          </Container>
        </Section>
      </Layout>
    </>
  );
};

export default withFacebook(withAmazon(SignInPage));
